import { useState, ChangeEvent, FormEvent, JSX, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useLogin } from "../../hooks/useAuth";
import { useAppSelector } from "../../store/hooks";
import styles from "./Login.module.css";
import logo from "../../assets/logo.png";

interface FormData {
  email: string;
  password: string;
}
interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const STATS = [
  { value: "500+", label: "Active Agencies" },
  { value: "50K+", label: "Monthly Bookings" },
  { value: "99.9%", label: "Uptime SLA" },
];

const QUOTES = [
  { text: "FlyGDS cut our setup time from months to minutes. Absolutely remarkable.", author: "Sarah M.", role: "CEO, SkyVoyage Travel" },
  { text: "The booking engine is rock-solid. Our clients love how seamless it feels.", author: "James T.", role: "Director, AirRoute Agency" },
  { text: "White-label branding made us look enterprise-grade from day one.", author: "Priya K.", role: "Founder, JetPath Solutions" },
];

function EyeToggle({ visible, onClick }: { visible: boolean; onClick: () => void }): JSX.Element {
  const p = { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <button type="button" onClick={onClick} className={styles.eyeBtn} tabIndex={-1} aria-label={visible ? "Hide" : "Show"}>
      {visible ? (<svg {...p}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>) : (<svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>)}
    </button>
  );
}

function ErrorMsg({ msg }: { msg: string }): JSX.Element {
  return (
    <p className={styles.errorMsg}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2" /><line x1="12" y1="16" x2="12.01" y2="16" stroke="white" strokeWidth="2" /></svg>
      {msg}
    </p>
  );
}

const Login = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { execute: loginUser, isLoading, error: apiError } = useLogin();
  const user = useAppSelector((s) => s.auth.user);

  const [form, setForm] = useState<FormData>({ email: "", password: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPw, setShowPw] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quoteAnim, setQuoteAnim] = useState(true);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setQuoteAnim(false);
      setTimeout(() => { setQuoteIdx((i) => (i + 1) % QUOTES.length); setQuoteAnim(true); }, 300);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (apiError) setErrors((p) => ({ ...p, general: apiError }));
  }, [apiError]);

  const filled = (v: string) => v.trim().length > 0;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name as keyof FormErrors]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.password) e.password = "Password is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const result = await loginUser(form.email.trim().toLowerCase(), form.password);
    if (result) {
      const from = (location.state as any)?.from?.pathname;
      if (result.user.role === "admin") {
        navigate(from || "/dashboard", { replace: true });
      } else {
        navigate(from || "/plans", { replace: true });
      }
    }
  };

  const inputClass = (name: string) =>
    [styles.inputWrap, focused === name ? styles.inputFocus : "", errors[name as keyof FormErrors] ? styles.inputError : "", filled(form[name as keyof FormData]) && !errors[name as keyof FormErrors] ? styles.inputSuccess : ""].join(" ").trim();

  const q = QUOTES[quoteIdx];

  return (
    <div className={`${styles.page} ${mounted ? styles.mounted : ""}`}>
      <aside className={styles.panel}>
        <div className={styles.panelBg}><div className={styles.panelNoise} /><div className={styles.g1} /><div className={styles.g2} /><div className={styles.g3} /><div className={styles.grid} /></div>
        <div className={styles.panelInner}>
          <div className={styles.logoWrap}><img src={logo} alt="FlyGDS" className={styles.logo} /><span className={styles.badge}><span className={styles.badgeDot} />Enterprise Platform</span></div>
          <div className={styles.hero}>
            <h1 className={styles.heroTitle}>Welcome back to your <span className={styles.accent}>travel command centre</span></h1>
            <p className={styles.heroDesc}>Manage bookings, monitor performance and grow your agency — all from one powerful dashboard.</p>
          </div>
          <div className={styles.statsRow}>{STATS.map((s, i) => (<div key={i} className={styles.stat}><div className={styles.statValue}>{s.value}</div><div className={styles.statLabel}>{s.label}</div></div>))}</div>
          <div className={`${styles.testimonial} ${quoteAnim ? styles.testimonialIn : styles.testimonialOut}`}>
            <div className={styles.quoteIcon}>"</div>
            <p className={styles.quoteText}>{q.text}</p>
            <div className={styles.quoteAuthor}><div className={styles.quoteAvatar}>{q.author[0]}</div><div><div className={styles.quoteName}>{q.author}</div><div className={styles.quoteRole}>{q.role}</div></div></div>
          </div>
        </div>
      </aside>

      <main className={styles.formSide}>
        <div className={styles.formSideBg} />
        <div className={styles.formContainer}>
          <div className={styles.topBar}><span className={styles.topBarText}>Don't have an account?</span><Link to="/register" className={styles.topBarLink}>Create account →</Link></div>
          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <div className={styles.lockIcon}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg></div>
              <h2 className={styles.title}>Sign in to FlyGDS</h2>
              <p className={styles.subtitle}>Enter your credentials to access your dashboard</p>
            </div>
            {errors.general && (<div className={styles.alert}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>{errors.general}</div>)}
            <form onSubmit={onSubmit} noValidate className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email Address</label>
                <div className={inputClass("email")}><span className={styles.inputIcon}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg></span><input id="email" name="email" type="email" value={form.email} onChange={onChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="john@acmetravel.com" className={styles.input} autoComplete="email" />{filled(form.email) && !errors.email && (<span className={styles.checkIcon}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></span>)}</div>
                {errors.email && <ErrorMsg msg={errors.email} />}
              </div>
              <div className={styles.field}>
                <div className={styles.labelRow}><label htmlFor="password" className={styles.label}>Password</label><Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link></div>
                <div className={inputClass("password")}><span className={styles.inputIcon}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg></span><input id="password" name="password" type={showPw ? "text" : "password"} value={form.password} onChange={onChange} onFocus={() => setFocused("password")} onBlur={() => setFocused(null)} placeholder="Enter your password" className={styles.input} autoComplete="current-password" /><EyeToggle visible={showPw} onClick={() => setShowPw((v) => !v)} /></div>
                {errors.password && <ErrorMsg msg={errors.password} />}
              </div>
              <label className={styles.rememberRow}><div className={styles.checkboxWrap}><input type="checkbox" className={styles.checkbox} /><span className={styles.checkboxCustom}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></span></div><span className={styles.rememberLabel}>Keep me signed in for 30 days</span></label>
              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? (<><span className={styles.spinner} /> Signing you in...</>) : (<>Sign In to Dashboard<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg></>)}
              </button>
            </form>
            <div className={styles.divider}><span>New to FlyGDS?</span></div>
            <Link to="/register" className={styles.registerLink}>Create a free account</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;